/** Checkout metadata that was wrongly stored as JSON inside orders.notes */
const METADATA_KEYS = new Set([
  'customer_phone',
  'payment_method',
  'payment_status',
  'invoice_required',
  'set_type',
]);

function isCheckoutMetadataObject(obj: unknown): boolean {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
  const keys = Object.keys(obj as Record<string, unknown>);
  if (keys.length === 0) return false;
  return keys.every((k) => METADATA_KEYS.has(k));
}

function findJsonObjectSpans(text: string): Array<{ start: number; end: number; raw: string }> {
  const spans: Array<{ start: number; end: number; raw: string }> = [];
  for (let i = 0; i < text.length; i++) {
    if (text[i] !== '{') continue;
    let depth = 0;
    let inString = false;
    let escape = false;
    for (let j = i; j < text.length; j++) {
      const ch = text[j];
      if (inString) {
        if (escape) {
          escape = false;
          continue;
        }
        if (ch === '\\') {
          escape = true;
          continue;
        }
        if (ch === '"') inString = false;
        continue;
      }
      if (ch === '"') {
        inString = true;
        continue;
      }
      if (ch === '{') depth += 1;
      else if (ch === '}') {
        depth -= 1;
        if (depth === 0) {
          spans.push({ start: i, end: j + 1, raw: text.slice(i, j + 1) });
          i = j;
          break;
        }
      }
    }
  }
  return spans;
}

function isCheckoutMetadataBlob(raw: string): boolean {
  const attempts = [raw, raw.replace(/[\r\n]+/g, ' '), raw.replace(/[\r\n]+/g, '\\n')];
  for (const candidate of attempts) {
    try {
      const parsed = JSON.parse(candidate);
      if (isCheckoutMetadataObject(parsed)) return true;
    } catch {
      /* try next */
    }
  }
  const keys = Array.from(raw.matchAll(/"([^"]+)"\s*:/g)).map((m) => m[1]);
  return (
    keys.length > 0 &&
    keys.every((k) => METADATA_KEYS.has(k)) &&
    keys.includes('customer_phone')
  );
}

/** Strip checkout JSON blobs so Order Notes shows only human text. */
export function stripCheckoutMetadataFromNotes(notes?: string | null): string {
  if (notes == null || notes === '') return '';
  let text = String(notes);
  const spans = findJsonObjectSpans(text);
  for (let i = spans.length - 1; i >= 0; i--) {
    const span = spans[i];
    if (!isCheckoutMetadataBlob(span.raw)) continue;
    text = text.slice(0, span.start) + text.slice(span.end);
  }
  text = text.replace(/[ \t]+\n/g, '\n').replace(/\n{2,}/g, '\n').trim();
  // Last resort: if leftovers still look like the checkout dump, hide entirely
  if (
    /["']customer_phone["']/.test(text) &&
    /["']payment_method["']/.test(text)
  ) {
    return '';
  }
  return text;
}

/** Notes text for UI — empty when only checkout metadata remains. */
export function formatOrderNotesForDisplay(notes?: string | null): string {
  return stripCheckoutMetadataFromNotes(notes);
}
